import React from 'react';
import { Card } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Settings } from 'lucide-react';
import { InfoTooltip } from '@/components/InfoTooltip';

export const GlobalSettings = ({ settings, onChange }) => {
  const handleChange = (field, value) => {
    onChange({ ...settings, [field]: parseFloat(value) });
  };

  return (
    <Card className="p-5 bg-white border-gray-200 shadow-md hover:shadow-lg transition-all">
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <div className="p-1.5 rounded-lg bg-purple-100">
            <Settings className="h-4 w-4 text-purple-600" />
          </div>
          <h3 className="font-semibold text-gray-800">Global Settings</h3>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className="text-xs text-gray-700 flex items-center">
              Electricity Rate ($/kWh)
              <InfoTooltip content="Your local electricity cost per kilowatt-hour. This determines how much it costs to power your 3D printer during operation. Check your utility bill for your actual rate." />
            </Label>
            <Input
              data-testid="kwh-rate-input"
              type="number"
              value={settings.kwh_rate}
              onChange={(e) => handleChange('kwh_rate', e.target.value)}
              step="0.01"
              className="bg-gray-50 border-gray-300 text-sm"
            />
          </div>

          <div className="space-y-2">
            <Label className="text-xs text-gray-700 flex items-center">
              Rent ($/year)
              <InfoTooltip content="Annual rent or space cost for your workshop/facility. This is divided across all prints based on floor space used and print time to calculate the floor space cost per print." />
            </Label>
            <Input
              data-testid="rent-input"
              type="number"
              value={settings.rent_year}
              onChange={(e) => handleChange('rent_year', e.target.value)}
              step="1000"
              className="bg-gray-50 border-gray-300 text-sm"
            />
          </div>

          <div className="space-y-2">
            <Label className="text-xs text-gray-700 flex items-center">
              Labour Rate ($/hr)
              <InfoTooltip content="Your hourly labor cost including wages, benefits, and overhead. Used to calculate the cost of manual tasks like starting prints, changing filament, and post-processing." />
            </Label>
            <Input
              data-testid="labour-rate-input"
              type="number"
              value={settings.labour_rate}
              onChange={(e) => handleChange('labour_rate', e.target.value)}
              step="1"
              className="bg-gray-50 border-gray-300 text-sm"
            />
          </div>

          <div className="space-y-2">
            <Label className="text-xs text-gray-700 flex items-center">
              Parts per Plate
              <InfoTooltip content="Number of identical parts printed on one build plate. Increasing this divides the setup and overhead costs across multiple parts, reducing per-part cost." />
            </Label>
            <Input
              data-testid="quantity-per-plate-input"
              type="number"
              value={settings.quantity_per_plate}
              onChange={(e) => handleChange('quantity_per_plate', e.target.value)}
              step="1"
              min="1"
              className="bg-gray-50 border-gray-300 text-sm"
            />
          </div>
        </div>
      </div>
    </Card>
  );
};